// src/components/checkout/ShippingForm.jsx

import React, { useEffect, useState } from 'react';
import Button from '../common/Button';

const DEFAULT_LOCATION = {
  city: 'Freetown',
  state: 'Western Area',
  country: 'Sierra Leone',
  zip: '000',
};

function ShippingForm({ userDetails, onSubmit }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    landmark: '',
    ...DEFAULT_LOCATION,
    to_default_address: false,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (!userDetails) return;

    const existingAddress =
      userDetails?.address?.street ||
      userDetails?.residential_address ||
      userDetails?.business_address ||
      userDetails?.address ||
      '';

    setFormData((current) => ({
      ...current,
      name:
        userDetails.name ||
        userDetails.full_name ||
        userDetails.business_name ||
        '',
      email: userDetails.email || '',
      phone:
        userDetails.phone_number ||
        userDetails.telephone ||
        userDetails.phone ||
        '',
      street:
        typeof existingAddress === 'string'
          ? existingAddress
          : '',
      landmark:
        userDetails?.address?.landmark ||
        userDetails?.landmark ||
        '',
      ...DEFAULT_LOCATION,
    }));
  }, [userDetails]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required.';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required.';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Enter a valid email address.';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required.';
    }

    if (!formData.street.trim()) {
      newErrors.street = 'Delivery address is required.';
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));

    setIsSubmitted(false);

    if (errors[name]) {
      setErrors((current) => ({
        ...current,
        [name]: '',
      }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    const consolidatedAddress = [
      formData.street.trim(),
      formData.landmark.trim(),
      DEFAULT_LOCATION.city,
      DEFAULT_LOCATION.country,
    ]
      .filter(Boolean)
      .join(', ');

    const shippingData = {
      ...formData,
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      street: formData.street.trim(),
      landmark: formData.landmark.trim(),

      // Fixed marketplace service location
      city: DEFAULT_LOCATION.city,
      state: DEFAULT_LOCATION.state,
      country: DEFAULT_LOCATION.country,
      zip: DEFAULT_LOCATION.zip,

      // Useful for invoices, receipts and delivery displays
      full_address: consolidatedAddress,
      formatted_address: consolidatedAddress,
    };

    onSubmit(shippingData, true);
    setIsSubmitted(true);
  };

  const inputClass = (field) =>
    `w-full rounded-xl border bg-white px-4 py-3.5 font-montserrat text-primary outline-none transition focus:ring-2 focus:ring-primary/30 ${
      errors[field]
        ? 'border-red-500'
        : 'border-gray-300 focus:border-primary'
    }`;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-md sm:p-7">
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-wider text-primary/60">
          Checkout
        </p>

        <h2 className="mt-1 font-playfair-display text-2xl font-semibold text-primary">
          Delivery Details
        </h2>

        <p className="mt-2 font-montserrat text-sm text-secondary">
          Your order will be delivered within Freetown, Sierra Leone.
        </p>

        <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-green-50 px-4 py-2 text-sm font-semibold text-green-700">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="h-4 w-4"
            aria-hidden="true"
          >
            <path d="M12 21s7-5.2 7-12a7 7 0 1 0-14 0c0 6.8 7 12 7 12Z" />
            <circle cx="12" cy="9" r="2.5" />
          </svg>

          Freetown, Sierra Leone
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <label
              htmlFor="shipping-name"
              className="mb-1.5 block font-montserrat text-sm font-semibold text-secondary"
            >
              Full Name *
            </label>

            <input
              id="shipping-name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={inputClass('name')}
              placeholder="Enter your full name"
              autoComplete="name"
            />

            {errors.name && (
              <p className="mt-1.5 text-sm text-red-600">
                {errors.name}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="shipping-phone"
              className="mb-1.5 block font-montserrat text-sm font-semibold text-secondary"
            >
              Phone Number *
            </label>

            <input
              id="shipping-phone"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={inputClass('phone')}
              placeholder="+232 76 123456"
              autoComplete="tel"
            />

            {errors.phone && (
              <p className="mt-1.5 text-sm text-red-600">
                {errors.phone}
              </p>
            )}
          </div>
        </div>

        <div>
          <label
            htmlFor="shipping-email"
            className="mb-1.5 block font-montserrat text-sm font-semibold text-secondary"
          >
            Email Address *
          </label>

          <input
            id="shipping-email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={inputClass('email')}
            placeholder="customer@example.com"
            autoComplete="email"
          />

          <p className="mt-1.5 text-xs text-gray-500">
            Your order confirmation and receipt will be sent here.
          </p>

          {errors.email && (
            <p className="mt-1.5 text-sm text-red-600">
              {errors.email}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="shipping-street"
            className="mb-1.5 block font-montserrat text-sm font-semibold text-secondary"
          >
            Delivery Address *
          </label>

          <textarea
            id="shipping-street"
            name="street"
            value={formData.street}
            onChange={handleChange}
            rows={3}
            className={`${inputClass('street')} resize-none`}
            placeholder="House number, street, area or community"
            autoComplete="street-address"
          />

          <p className="mt-1.5 text-xs text-gray-500">
            Example: 15 Wilkinson Road, near the main junction.
          </p>

          {errors.street && (
            <p className="mt-1.5 text-sm text-red-600">
              {errors.street}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="shipping-landmark"
            className="mb-1.5 block font-montserrat text-sm font-semibold text-secondary"
          >
            Landmark or Delivery Instructions
            <span className="ml-1 font-normal text-gray-400">
              (optional)
            </span>
          </label>

          <input
            id="shipping-landmark"
            type="text"
            name="landmark"
            value={formData.landmark}
            onChange={handleChange}
            className={inputClass('landmark')}
            placeholder="Opposite the pharmacy, blue gate"
          />
        </div>

        <Button
          type="submit"
          className="mt-6 w-full rounded-full bg-primary px-6 py-3.5 font-montserrat text-lg text-cream hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
          disabled={isSubmitted}
        >
          {isSubmitted
            ? 'Delivery Details Saved ✓'
            : 'Save & Continue to Payment'}
        </Button>
      </form>
    </div>
  );
}

export default ShippingForm;